import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "utilities/auth";
import { getUserType } from "utilities/userType";
import PageLoader from "../Loaders/PageLoader";

/**
 * Gate a route on the signed-in user's type.
 *
 * `auth.user` is THREE-STATE, and all three have to be handled separately:
 *
 *   null   — still resolving. Firebase has a session but the merged user
 *            document has not arrived (or does not exist yet).
 *   false  — definitively signed out.
 *   object — signed in and provisioned.
 *
 * SOUND-726. This used to read `const { user, loading } = useAuth()` — but
 * `useAuthProvider` has NEVER returned a `loading` key (see the return block in
 * utilities/auth.js). So `loading` was permanently `undefined`, the "Loading…"
 * branch below was dead code, and both `null` and `false` fell through to
 * `if (!user) return null` — rendering NOTHING.
 *
 * That is the black screen a prospective client reported on 2026-08-28. Signup
 * redirects to /registration, which is gated by this component, and a fresh
 * signup sits at `user === null` until its email is confirmed and the account
 * provisioned. So the merchant landed on a route that rendered an empty page on
 * a dark background: no spinner, no message, no way back to sign-in. A signed
 * OUT visitor to any of these 58 routes got the same nothing.
 *
 * The sibling gate `requireAuth` in utilities/auth.js has always distinguished
 * the three states correctly. This now matches it: loader while resolving,
 * redirect to sign-in when signed out.
 */
export function RequireRole({ role, children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const resolving = user === null;

  // --- Effect Phase (redirects only) ---
  useEffect(() => {
    if (resolving) return;

    // Signed out. Send them somewhere they can act, rather than rendering an
    // empty page and leaving them to guess.
    if (!user) {
      navigate("/auth/signin", { replace: true });
      return;
    }

    const userType = getUserType(user);
    const rolesAllowed = Array.isArray(role) ? role : [role];
    if (!rolesAllowed.includes(userType)) {
      switch (userType) {
        case "client":
          navigate("/dashboard", { replace: true });
          break;
        case "internal":
          navigate("/companies", { replace: true });
          break;
        case "partner":
          navigate("/companies", { replace: true });
          break;
        case "processor":
          // Processors (SOUND-559) live in the risk/monitoring surface only;
          // anything else lands them back on their home, never /companies.
          navigate("/risk", { replace: true });
          break;
        default:
          navigate("/", { replace: true });
          break;
      }
    }
  }, [user, role, resolving, navigate]);

  // --- Render Phase (UI gating only) ---
  // Resolving: show the loader. A merchant mid-signup can sit here for a while
  // — provisioning waits on them confirming their email — and an empty page
  // reads as a broken site, which is exactly how this was reported.
  if (resolving) {
    return <PageLoader />;
  }

  // Signed out. The effect above is already navigating to sign-in; render
  // nothing for the frame in between rather than flashing the gated page.
  if (!user) {
    return null;
  }

  const userType = getUserType(user);
  const rolesAllowed = Array.isArray(role) ? role : [role];
  if (!rolesAllowed.includes(userType)) {
    return null;
  }
  return children;
}
